import clamp from 'lodash/clamp';
import get from 'lodash/get';
import update from 'lodash/update';
import { useCallback, useEffect, useState } from 'react';

import {
  QuantityLimitIds,
  areQuantityLimitIdsEqual,
  calculateQuantityLimitIds,
  shouldClearSelection,
  shouldPreventRemoval,
} from './useSidePanelSectionTab.utils';

import { useClearSelectionCircles } from '~/core/hooks/useClearSelectionCircles';

type OptionId = string | number;
type Maybe<T> = T | undefined;

type DisplayData = {
  title: string;
  image: string;
  price: string;
  isDisabled?: boolean;
};

type OptionData<T> = {
  data: T;
  display: DisplayData;
};

export type useSidePanelSectionTabArgs<T> = {
  options: T[];
  optionsCounters: Record<OptionId, number>;
  isFetching?: boolean;
  getId: (option: T) => OptionId;
  mapOptionToDisplay: (option: T) => DisplayData;
  defaultOption?: T;
  ignoreDefaultOptionOnRemoval?: boolean;
  stateAddOption: (option: T) => void;
  stateRemoveOption: (option: T) => void;
  countersLimit?: number;
};

export const useSidePanelSectionTab = <T>({
  countersLimit = 999,
  options,
  optionsCounters,
  isFetching = false,
  getId,
  mapOptionToDisplay,
  defaultOption,
  ignoreDefaultOptionOnRemoval = false,
  stateAddOption,
  stateRemoveOption,
}: useSidePanelSectionTabArgs<T>) => {
  const [isOpenRemoveModal, setIsOpenRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<Maybe<OptionId>>();
  const [optionIndexOn3DModal, setOptionIndexOn3DModal] =
    useState<Maybe<number>>();
  const [prevQtyLimitIds, setPrevQtyLimitIds] = useState<QuantityLimitIds>({
    oneToZero: [],
    oneToMax: [],
  });
  const optionsById = options.reduce<Record<OptionId, OptionData<T>>>(
    (z, option) =>
      update(z, getId(option), () => ({
        data: option,
        display: mapOptionToDisplay(option),
      })),
    {}
  );
  const getQty = useCallback(
    (id?: OptionId): number =>
      clamp(get(optionsCounters, id ?? -1, 0), 0, countersLimit),
    [optionsCounters, countersLimit]
  );
  const defaultOptionId = defaultOption ? getId(defaultOption) : undefined;
  const selectedOptionQty = getQty(selectedOptionId);
  const isSelectedOptionAtMaxQty = selectedOptionQty === countersLimit;
  const isSelectedOptionAtZeroQty = selectedOptionQty === 0;
  const isSelectedOption = useCallback(
    (id: OptionId) => selectedOptionId === id,
    [selectedOptionId]
  );

  const openRemoveModal = useCallback(() => setIsOpenRemoveModal(true), []);
  const closeRemoveModal = useCallback(() => setIsOpenRemoveModal(false), []);

  const prepareToAdd = (optionId: OptionId, qty: number) => {
    if (qty >= countersLimit) {
      return undefined;
    }

    return () => {
      stateAddOption(optionsById[optionId].data);
      setSelectedOptionId(optionId);
      setIsRemoving(false);
    };
  };

  const prepareToRemove = (optionId: OptionId) => {
    if (
      shouldPreventRemoval(
        optionId,
        defaultOptionId,
        isSelectedOption(optionId),
        isSelectedOptionAtZeroQty
      )
    ) {
      return undefined;
    }

    return () => {
      const option = ignoreDefaultOptionOnRemoval
        ? optionsById[optionId].data
        : (defaultOption ?? optionsById[optionId].data);

      openRemoveModal();
      stateRemoveOption(option);
      setSelectedOptionId(optionId);
      setIsRemoving(true);
    };
  };

  const { clearSelectionCircles: _clearSelectionCircles } =
    useClearSelectionCircles();

  const clearSelectionCircles = useCallback(() => {
    _clearSelectionCircles();
    setIsRemoving(false);
  }, [_clearSelectionCircles]);

  /**
   * clear selection circles (blue dots) on mount and unmount
   */
  useEffect(() => {
    clearSelectionCircles();

    return () => clearSelectionCircles();
  }, [clearSelectionCircles]);

  /**
   * Clear selection circles (blue dots) when corner options have reached its
   * limit, meaning: no more options can be applied to any shape
   */
  useEffect(() => {
    const currentLimitIds = calculateQuantityLimitIds(
      optionsCounters,
      countersLimit
    );

    const shouldClear = shouldClearSelection({
      selectedOptionId,
      prevQtyLimitIds,
      isSelectedOptionAtMaxQty,
      isSelectedOptionAtZeroQty,
      isRemoving,
    });

    if (shouldClear) {
      setSelectedOptionId(undefined);
      clearSelectionCircles();
    }

    const limitsChanged = !areQuantityLimitIdsEqual(
      currentLimitIds,
      prevQtyLimitIds
    );

    if (limitsChanged) {
      setPrevQtyLimitIds(currentLimitIds);
    }
  }, [
    optionsCounters,
    countersLimit,
    selectedOptionId,
    prevQtyLimitIds,
    isSelectedOptionAtMaxQty,
    isSelectedOptionAtZeroQty,
    isRemoving,
    clearSelectionCircles,
  ]);

  return {
    isFetching,
    options,
    modalFor3DCarousel: {
      isOpen: optionIndexOn3DModal !== undefined,
      optionIndex: optionIndexOn3DModal,
      setOptionIndex: setOptionIndexOn3DModal,
      close: () => setOptionIndexOn3DModal(undefined),
    },
    modalForOptionRemoval: {
      isOpen: isOpenRemoveModal,
      close: closeRemoveModal,
    },
    getQty,
    prepareToAdd,
    prepareToRemove,
    optionsById,
    selectedOptionId,
    setSelectedOptionId,
  };
};
